import { useEffect, useState, type FormEvent, type ReactNode } from 'react';
import { apiRequest, ApiError } from '../../api/client.js';
import { useAuth } from '../../auth/AuthProvider.js';
import { useTranslation } from '../../../../shared/i18n/index.js';
import { useBusinessNumber } from '../../../../shared/hooks/useBusinessNumber.js';
import { Button } from '../../../../shared/components/index.js';

interface SearchHit {
  readonly searchIndexId: string;
  readonly indexCode: string;
  readonly sourceModule: string;
  readonly contentType: string;
  readonly sourceEntityType: string;
  readonly sourceEntityId: string;
  readonly title: string;
  readonly snippet: string;
  readonly classificationLevel: string;
  readonly category: string | null;
  readonly statusLabel: string | null;
  readonly urlRef: string | null;
  readonly rank: number;
  readonly relevanceScore: number;
  readonly rankingFactors: Record<string, number>;
}
interface SearchOutcome {
  readonly searchQueryId: string;
  readonly queryCode: string;
  readonly query: string;
  readonly searchType: string;
  readonly status: string;
  readonly resultCount: number;
  readonly executionMs: number;
  readonly page: number;
  readonly pageSize: number;
  readonly results: readonly SearchHit[];
}
interface FacetRow {
  readonly facetType: string;
  readonly facetKey: string;
  readonly facetValue: string;
  readonly count: number;
}

const MODULES = ['DMS', 'KMS', 'DAM', 'ANALYTICS', 'CONTRACTS', 'CAMPAIGNS', 'VENDORS', 'WORKFLOWS', 'APPROVALS', 'PROJECTS'];
const SEARCH_TYPES = ['KEYWORD', 'PHRASE', 'BOOLEAN', 'WILDCARD', 'FUZZY', 'NATURAL_LANGUAGE', 'FILTERED'];

/**
 * M39 — Universal Search Portal (§28/§23). The primary discovery surface: a single
 * query box runs a governed keyword search across every indexed module (T514) and
 * returns ACL-trimmed, relevance-ranked hits (§13). A module filter narrows the
 * scope and drives faceted-navigation counts (§12). Selecting a result records a
 * click-through (§18) so popularity and analytics stay live. All queries require
 * SEARCH_EXECUTE; tenant isolation and permission trimming are enforced
 * server-side (LAW 4 / BR-01). A 403 surfaces a clear permission message.
 */
export function UniversalSearchPortal(): ReactNode {
  const { token } = useAuth();
  const { t } = useTranslation();

  const { number } = useBusinessNumber();
  const [query, setQuery] = useState('');
  const [searchType, setSearchType] = useState('KEYWORD');
  const [module, setModule] = useState('');
  const [outcome, setOutcome] = useState<SearchOutcome | null>(null);
  const [facets, setFacets] = useState<FacetRow[]>([]);
  const [facetsError, setFacetsError] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const [clicked, setClicked] = useState<string | null>(null);

  const runSearch = async (e: FormEvent): Promise<void> => {
    e.preventDefault();
    setError(null);
    setClicked(null);
    if (!query.trim()) { setError(t('m39.universal.enterQuery')); return; }
    setBusy(true);
    try {
      const params = new URLSearchParams();
      params.set('q', query.trim());
      params.set('type', searchType);
      if (module) params.set('modules', module);
      const r = await apiRequest<{ data: SearchOutcome }>(`/v1/search?${params.toString()}`, { token });
      setOutcome(r.data);
    } catch (err) {
      if (err instanceof ApiError && err.status === 403) {
        setError(t('m39.universal.notAuthorised'));
      } else {
        setError((err as Error).message);
      }
      setOutcome(null);
    } finally {
      setBusy(false);
    }
  };

  useEffect(() => {
    let alive = true;
    const params = new URLSearchParams();
    if (module) params.set('module', module);
    apiRequest<{ data: FacetRow[] }>(`/v1/search/facets?${params.toString()}`, { token })
      .then((r) => { if (alive) { setFacets(r.data); setFacetsError(null); } })
      // Unread facets are not "no facets".
      .catch((err) => {
        if (!alive) return;
        setFacets([]);
        setFacetsError((err as Error).message);
      });
    return () => { alive = false; };
  }, [token, module]);

  const recordClick = async (hit: SearchHit): Promise<void> => {
    if (!outcome) return;
    try {
      await apiRequest(`/v1/search/${outcome.searchQueryId}/click/${hit.searchIndexId}`, { method: 'POST', token });
      setClicked(hit.indexCode);
    } catch {
      /* click tracking is best-effort; never blocks navigation */
    }
  };

  return (
    <main aria-label="universal-search-portal">
      <h1>{t('m39.universal.title')}</h1>

      <form aria-label="search-form" onSubmit={(e) => void runSearch(e)}>
        <label>{t('m39.common.query')}
          <input aria-label="field-query" value={query} onChange={(e) => setQuery(e.target.value)} placeholder={t('m39.universal.queryPlaceholder')} />
        </label>
        <label>{t('m39.common.type')}
          <select aria-label="field-type" value={searchType} onChange={(e) => setSearchType(e.target.value)}>
            {SEARCH_TYPES.map((ty) => <option key={ty} value={ty}>{ty}</option>)}
          </select>
        </label>
        <label>{t('m39.common.module')}
          <select aria-label="field-module" value={module} onChange={(e) => setModule(e.target.value)}>
            <option value="">{t('m39.common.allModules')}</option>
            {MODULES.map((m) => <option key={m} value={m}>{m}</option>)}
          </select>
        </label>
        <Button variant="primary" type="submit" aria-label="submit-search" disabled={busy}>{busy ? t('m39.universal.searching') : t('common.search')}</Button>
      </form>

      {error && <p role="alert" aria-label="search-error">{error}</p>}

      <div className="search-layout">
        <aside aria-label="search-facets">
          <h2>{t('m39.universal.refine')}</h2>
          <ul>
            {facetsError ? (
              <li role="alert" aria-label="facets-error">{facetsError}</li>
            ) : facets.length === 0 ? (
              <li aria-label="facets-empty">{t('m39.universal.facetsEmpty')}</li>
            ) : facets.map((f) => (
              <li key={`${f.facetType}:${f.facetValue}`} data-facet={f.facetValue}>
                <span>{f.facetType}</span> · <span>{f.facetValue}</span> <span aria-label={`facet-count-${f.facetValue}`}>({f.count})</span>
              </li>
            ))}
          </ul>
        </aside>

        <section aria-label="search-results">
          {!outcome ? (
            <p aria-label="results-idle">{t('m39.universal.idle')}</p>
          ) : (
            <>
              <p aria-label="results-summary">
                {t('m39.universal.summary', { count: outcome.resultCount, query: outcome.query, ms: outcome.executionMs, status: outcome.status })}
              </p>
              {clicked && <p aria-label="click-recorded">{t('m39.universal.clickRecorded', { code: clicked })}</p>}
              {outcome.results.length === 0 ? (
                <p aria-label="results-empty">{t('m39.universal.empty')}</p>
              ) : (
                <ol aria-label="results-list">
                  {outcome.results.map((hit) => (
                    <li key={hit.searchIndexId} data-result={hit.indexCode}>
                      <Button variant="secondary" type="button" aria-label={`open-${hit.indexCode}`} onClick={() => void recordClick(hit)}>
                        {hit.title}
                      </Button>
                      <span aria-label={`module-${hit.indexCode}`}>{hit.sourceModule}</span>
                      <span aria-label={`class-${hit.indexCode}`}>{hit.classificationLevel}</span>
                      <span aria-label={`score-${hit.indexCode}`}>{number(hit.relevanceScore, 3)}</span>
                      <p>{hit.snippet}</p>
                    </li>
                  ))}
                </ol>
              )}
            </>
          )}
        </section>
      </div>
    </main>
  );
}
